import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, BookOpen, Eye, Search } from "lucide-react";
import { apiFetch, API } from "@/utils/api";

function formatCurrency(amount) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("es-ES");
}

function EntryForm({ accounts, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    description: "",
    lines: [
      { account_code: "", debit: "", credit: "", description: "" },
      { account_code: "", debit: "", credit: "", description: "" },
    ],
  });

  const totalDebit = formData.lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
  const totalCredit = formData.lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { account_code: "", debit: "", credit: "", description: "" }],
    });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    setFormData({ ...formData, lines: newLines });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.description) {
      toast.error("La descripción es obligatoria");
      return;
    }
    if (!isBalanced) {
      toast.error("El asiento no está cuadrado");
      return;
    }
    if (totalDebit === 0) {
      toast.error("El asiento debe tener importes");
      return;
    }

    const validLines = formData.lines.filter(
      (l) => l.account_code && (parseFloat(l.debit) > 0 || parseFloat(l.credit) > 0)
    );

    if (validLines.length < 2) {
      toast.error("El asiento necesita al menos 2 líneas");
      return;
    }

    onSave({
      ...formData,
      lines: validLines.map((l) => ({
        account_code: l.account_code,
        debit: parseFloat(l.debit) || 0,
        credit: parseFloat(l.credit) || 0,
        description: l.description,
      })),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Fecha *</Label>
          <Input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            data-testid="entry-date-input"
          />
        </div>
        <div>
          <Label>Descripción *</Label>
          <Input
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Descripción del asiento"
            data-testid="entry-description-input"
          />
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-[200px]">Cuenta</TableHead>
              <TableHead className="w-[200px]">Concepto</TableHead>
              <TableHead className="w-[120px] text-right">Debe</TableHead>
              <TableHead className="w-[120px] text-right">Haber</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {formData.lines.map((line, idx) => (
              <TableRow key={idx}>
                <TableCell className="p-2">
                  <Select
                    value={line.account_code}
                    onValueChange={(value) => updateLine(idx, "account_code", value)}
                  >
                    <SelectTrigger className="h-8" data-testid={`line-account-${idx}`}>
                      <SelectValue placeholder="Cuenta..." />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map((acc) => (
                        <SelectItem key={acc.code} value={acc.code}>
                          {acc.code} - {acc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="p-2">
                  <Input
                    value={line.description}
                    onChange={(e) => updateLine(idx, "description", e.target.value)}
                    placeholder="Concepto"
                    className="h-8"
                    data-testid={`line-desc-${idx}`}
                  />
                </TableCell>
                <TableCell className="p-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={line.debit}
                    onChange={(e) => updateLine(idx, "debit", e.target.value)}
                    placeholder="0.00"
                    className="h-8 text-right font-mono"
                    data-testid={`line-debit-${idx}`}
                  />
                </TableCell>
                <TableCell className="p-2">
                  <Input
                    type="number"
                    step="0.01"
                    value={line.credit}
                    onChange={(e) => updateLine(idx, "credit", e.target.value)}
                    placeholder="0.00"
                    className="h-8 text-right font-mono"
                    data-testid={`line-credit-${idx}`}
                  />
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-slate-50 font-medium">
              <TableCell colSpan={2} className="text-right">
                Totales:
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(totalDebit)}
              </TableCell>
              <TableCell className="text-right font-mono">
                {formatCurrency(totalCredit)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <Button type="button" variant="outline" onClick={addLine} size="sm">
          <Plus className="w-4 h-4 mr-1" />
          Añadir línea
        </Button>
        <div className="flex items-center gap-2">
          {isBalanced ? (
            <Badge className="bg-emerald-100 text-emerald-700">Cuadrado</Badge>
          ) : (
            <Badge className="bg-rose-100 text-rose-700">
              Descuadre: {formatCurrency(Math.abs(totalDebit - totalCredit))}
            </Badge>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={!isBalanced} data-testid="save-entry-btn">
          Crear Asiento
        </Button>
      </DialogFooter>
    </form>
  );
}

function EntryDetailDialog({ entry, accounts, onClose }) {
  const accountMap = accounts.reduce((m, a) => ({ ...m, [a.code]: a.name }), {});

  const totalDebit = entry.lines.reduce((sum, l) => sum + (l.debit || 0), 0);
  const totalCredit = entry.lines.reduce((sum, l) => sum + (l.credit || 0), 0);

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle className="font-heading text-lg">
          Asiento #{entry.entry_number} - {formatDate(entry.date)}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <div className="bg-slate-50 rounded-lg px-4 py-3">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-1">Descripcion</p>
          <p className="font-medium text-slate-900 text-sm">{entry.description}</p>
        </div>

        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-[100px]">Cuenta</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Nombre</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Concepto</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider w-[110px]">Debe</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider w-[110px]">Haber</th>
              </tr>
            </thead>
            <tbody>
              {entry.lines.map((line, idx) => (
                <tr key={idx} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-4 py-2.5 font-mono text-blue-600 text-xs font-semibold">{line.account_code}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">{accountMap[line.account_code] || ""}</td>
                  <td className="px-4 py-2.5 text-slate-800 text-xs">{line.description}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs font-medium">
                    {line.debit > 0 ? formatCurrency(line.debit) : ""}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs font-medium">
                    {line.credit > 0 ? formatCurrency(line.credit) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t border-slate-200">
                <td colSpan={3} className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase">Totales</td>
                <td className="px-4 py-2.5 text-right font-mono text-xs font-bold text-slate-900">{formatCurrency(totalDebit)}</td>
                <td className="px-4 py-2.5 text-right font-mono text-xs font-bold text-slate-900">{formatCurrency(totalCredit)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="flex justify-between text-xs text-slate-400 px-1">
          <span>Periodo: {entry.period}</span>
          <span>Origen: {entry.source_type === "invoice_emitted" ? "Factura emitida" : entry.source_type === "invoice_received" ? "Factura recibida" : entry.source_type || "Manual"}</span>
        </div>
      </div>

      <DialogFooter>
        <Button onClick={onClose} variant="outline">Cerrar</Button>
      </DialogFooter>
    </DialogContent>
  );
}

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailEntry, setDetailEntry] = useState(null);
  const [periodFilter, setPeriodFilter] = useState(new Date().toISOString().slice(0, 7));

  const fetchEntries = async () => {
    try {
      const params = new URLSearchParams({ period: periodFilter });
      const response = await apiFetch(`${API}/accounting/journal?${params}`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data.entries || []);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await apiFetch(`${API}/accounting/chart`);
      if (response.ok) {
        const data = await response.json();
        setAccounts(data.accounts || []);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [periodFilter]);

  const handleSaveEntry = async (formData) => {
    try {
      const response = await apiFetch(`${API}/accounting/entries`, {
        method: "POST",
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Asiento creado");
        setDialogOpen(false);
        fetchEntries();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Error al crear asiento");
      }
    } catch (error) {
      toast.error("Error al crear asiento");
    }
  };

  const accountMap = accounts.reduce((m, a) => ({ ...m, [a.code]: a.name }), {});

  // Calculate totals
  const totals = entries.reduce(
    (acc, entry) => {
      entry.lines.forEach((line) => {
        acc.debit += line.debit || 0;
        acc.credit += line.credit || 0;
      });
      return acc;
    },
    { debit: 0, credit: 0 }
  );

  return (
    <div className="space-y-6" data-testid="journal-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Libro Diario
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {entries.length} asientos en {periodFilter}
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="new-entry-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Asiento
        </Button>
      </div>

      {/* Period Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-end">
            <div className="w-48">
              <Label className="mb-2 block">Período</Label>
              <Input
                type="month"
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                data-testid="period-filter"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Journal Entries */}
      <Card>
        <CardContent className="p-0">
          <Table className="table-dense min-w-[1300px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">Nº</TableHead>
                <TableHead className="w-[100px]">Fecha</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead className="w-[100px]">Cuenta</TableHead>
                <TableHead className="text-right w-[120px]">Debe</TableHead>
                <TableHead className="text-right w-[120px]">Haber</TableHead>
                <TableHead className="w-[80px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="spinner mx-auto" />
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay asientos en este período</p>
                  </TableCell>
                </TableRow>
              ) : (
                entries.flatMap((entry) =>
                  entry.lines.map((line, lineIdx) => (
                    <TableRow key={`${entry.entry_id}-${lineIdx}`} data-testid={`entry-row-${entry.entry_id}`}>
                      {lineIdx === 0 ? (
                        <>
                          <TableCell rowSpan={entry.lines.length} className="font-mono font-medium align-top">
                            {entry.entry_number}
                          </TableCell>
                          <TableCell rowSpan={entry.lines.length} className="font-mono align-top">
                            {formatDate(entry.date)}
                          </TableCell>
                          <TableCell rowSpan={entry.lines.length} className="align-top">
                            {entry.description}
                          </TableCell>
                        </>
                      ) : null}
                      <TableCell className="font-mono text-blue-600">
                        {line.account_code}
                      </TableCell>
                      <TableCell className="numeric">
                        {line.debit > 0 ? formatCurrency(line.debit) : ""}
                      </TableCell>
                      <TableCell className="numeric">
                        {line.credit > 0 ? formatCurrency(line.credit) : ""}
                      </TableCell>
                      {lineIdx === 0 ? (
                        <TableCell rowSpan={entry.lines.length} className="align-top">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDetailEntry(entry)}
                            data-testid={`view-entry-${entry.entry_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                )
              )}
              {entries.length > 0 && (
                <TableRow className="bg-slate-50 font-medium">
                  <TableCell colSpan={4} className="text-right">
                    Totales período:
                  </TableCell>
                  <TableCell className="numeric">{formatCurrency(totals.debit)}</TableCell>
                  <TableCell className="numeric">{formatCurrency(totals.credit)}</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* New Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Nuevo Asiento Manual</DialogTitle>
          </DialogHeader>
          <EntryForm
            accounts={accounts}
            onSave={handleSaveEntry}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Entry Detail Dialog */}
      <Dialog open={!!detailEntry} onOpenChange={() => setDetailEntry(null)}>
        {detailEntry && (
          <EntryDetailDialog
            entry={detailEntry}
            accounts={accounts}
            onClose={() => setDetailEntry(null)}
          />
        )}
      </Dialog>
    </div>
  );
}
